/**
 * ExpressionEngine.js
 * Generates procedural CC curves for expression and timbre.
 * Adds 'breathing life' to static notes.
 */

import { getBeatStrength } from '../RhythmLogic.js';

/**
 * Applies procedural expression curves to events.
 * @param {Array} events - The generated note events.
 * @param {Object} context - Generation context.
 * @returns {Array} - The events array with injected CC events.
 */
export const applyExpressionCurves = (events, context) => {
    const ccEvents = [];
    const eventsByInstrument = {};

    // Group by Instrument
    events.forEach(ev => {
        if (!ev.instrumentId) return;
        if (!eventsByInstrument[ev.instrumentId]) eventsByInstrument[ev.instrumentId] = [];
        eventsByInstrument[ev.instrumentId].push(ev);
    });

    Object.entries(eventsByInstrument).forEach(([instId, instEvents]) => {
        const id = instId.toLowerCase();

        // 1. Strings / Winds / Brass: CC11 Expression Swells
        const isOrchestral = id.includes('string') || id.includes('violin') || id.includes('cello') ||
                             id.includes('flute') || id.includes('oboe') || id.includes('horn') ||
                             id.includes('brass') || id.includes('trumpet');

        if (isOrchestral) {
             // Task 1: Check for Terraced Dynamics
             // If genre is Baroque or velocity strategy is 'terraced', DISABLE swells.
             // We detect this via the first event's velocity or context metadata if available.
             // Heuristic: If velocity is quantized (0.3/0.6/0.9) it might be terraced.
             // Better: Passed in context? No, context is global.
             // Let's check context.traits.name === 'Baroque'.
             const isTerraced = (context.traits && context.traits.name === 'Baroque');

             instEvents.forEach(ev => {
                 // Skip Swells for Terraced Dynamics
                 if (isTerraced) {
                     // Just emit a static CC to match velocity
                     ccEvents.push({
                         s: ev.s,
                         type: 'cc',
                         cc: 11,
                         val: ev.vel || 0.7, // Match velocity
                         instrumentId: instId,
                         channel: ev.channel || 0,
                         role: 'expression_terraced'
                     });
                     return;
                 }

                 // Check if note is long enough for a swell (> 2.0 beats)
                 if (ev.len >= 2.0 && ev.d !== undefined) {
                     // Generate Swell
                     // CC11 (Expression)
                     // Connect Timbral Motion Vector
                     const motion = (context.config && context.config.vectors) ? context.config.vectors.timbralMotion : 0.5;

                     // Scale depth based on motion (0.1 -> Flat, 0.9 -> Deep)
                     const depth = 0.1 + (motion * 0.8); // 0.1 to 0.9 range
                     const center = 0.65;

                     const startVal = Math.max(0.2, center - (depth / 2));
                     const peakVal = Math.min(1.0, center + (depth / 2));

                     // Sample points every 0.25 beats
                     const steps = Math.floor(ev.len / 0.25);
                     const channel = ev.channel || 0;

                     for(let i=0; i<=steps; i++) {
                         const progress = i / steps;
                         let val = startVal;

                         // Arch shape: sin(0..PI) -> 0..1..0
                         // Non-Linear Swell: Peak later in the note (0.7) rather than center (0.5)
                         // Warping time: t' = t^1.5 (shifts center right) or t^0.7 (shifts left)
                         // We want a slow build and fast decay? Or musical swell?
                         // Musical swell usually peaks at ~66%.
                         // Use Beta distribution approximation or just warp sine.
                         const warpedP = Math.pow(progress, 0.7); // Shift peak earlier? No, sin(x^k)

                         // Simple: sin(p * PI) is symmetric.
                         // Skewed: sin(p^k * PI)

                         val = startVal + (Math.sin(progress * Math.PI) * (peakVal - startVal));

                         // Apply Gamma Correction to value (Exp)
                         // Makes low values linger longer
                         // val = Math.pow(val, 1.5); // Range issue if startVal > 0

                         ccEvents.push({
                             s: ev.s + (i * 0.25),
                             type: 'cc',
                             cc: 11,
                             val: val,
                             instrumentId: instId,
                             channel: channel,
                             role: 'expression'
                         });
                     }
                 }
             });

             // FIX: Silence Bug (Ghost Notes)
             // Short notes (< 2.0 beats) were skipped above, receiving no CC11.
             // If the synth defaults CC11 to 0, these notes are silent.
             // Inject a static "Baseline Dynamics" event for every note that didn't get a curve.
             instEvents.forEach(ev => {
                 if (ev.d !== undefined && ev.len < 2.0) {
                     // Check if we already generated a CC event at this time (unlikely for short notes)
                     // Optimization: Just blast a value. The renderer handles dense CCs.
                     ccEvents.push({
                         s: ev.s,
                         type: 'cc',
                         cc: 11,
                         val: 0.7, // Healthy baseline (mf)
                         instrumentId: instId,
                         channel: ev.channel || 0,
                         role: 'expression_baseline'
                     });
                 }
             });
        }

        // 2. Synths: CC74 Filter Cutoff based on Tension
        const isSynth = id.includes('synth') || id.includes('pad') || id.includes('lead');
        if (isSynth) {
            // Global Tension Curve Analysis
            // Iterate through the time range of the instrument's activity
            // to inject continuous CC data (not just per note)

            const first = instEvents[0];
            const last = instEvents[instEvents.length-1];

            if(first && last) {
                const start = Math.floor(first.s);
                const end = Math.ceil(last.s + (last.len || 0));
                const channel = first.channel || 0;

                for(let t=start; t<end; t+=0.5) { // Every 8th note resolution
                    const tension = context.getTension ? context.getTension(t/4) : 0.5;

                    // Map tension 0..1 to CC74 0.2..0.9
                    // Non-Linear Mapping: Filters open exponentially
                    // 0.2 + 0.7 * t^2
                    const cutoff = 0.2 + (Math.pow(tension, 2) * 0.7);

                    ccEvents.push({
                         s: t,
                         type: 'cc',
                         cc: 74,
                         val: cutoff,
                         instrumentId: instId,
                         channel: channel,
                         role: 'expression'
                    });
                }
            }
        }
    });

    // Merge and sort
    return [...events, ...ccEvents].sort((a,b) => a.s - b.s);
};
